import React, { useState } from "react";
import {
  SwipeableDrawer,
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChairIcon from "@mui/icons-material/Chair";

const SEAT_TYPES = {
  1: "Window 🪟",
  2: "Aisle 🚶‍♂️",
  3: "Middle 💺",
};

const SEAT_DESCRIPTION = {
  1: "Included",
  2: "Extra Cost",
};

const SeatDrawer = ({ open, onClose, onOpen, seatDynamicData = [], passengerCount = 1, onSeatSelection }) => {
  const [selectedSeats, setSelectedSeats] = useState({});
  console.log("selectedSeats", selectedSeats);
  const [totalPrice, setTotalPrice] = useState(0);

  const toggleSeatSelection = (segmentIndex, seat) => {
    setSelectedSeats((prev) => {
      const currentSegment = prev[segmentIndex] || [];
      const isSelected = currentSegment.some((s) => s.Code === seat.Code);
      
      // If seat is already selected, remove it
      if (isSelected) {
        const updatedSegment = currentSegment.filter((s) => s.Code !== seat.Code);
        const newSelectedSeats = { ...prev, [segmentIndex]: updatedSegment };
        const newTotalPrice = Object.values(newSelectedSeats)
          .flat()
          .reduce((sum, s) => sum + s.Price, 0);
        setTotalPrice(newTotalPrice);
        onSeatSelection(newSelectedSeats);
        return newSelectedSeats;
      }
      
      // Check if we can select more seats based on passenger count
      const totalSelectedSeats = Object.values(prev)
        .flat()
        .length;
      
      if (totalSelectedSeats >= passengerCount) {
        // Show error or notification that seat limit reached
        return prev;
      }

      const updatedSegment = [...currentSegment, seat];
      const newSelectedSeats = { ...prev, [segmentIndex]: updatedSegment };
      
      // Update total price
      const newTotalPrice = Object.values(newSelectedSeats)
        .flat()
        .reduce((sum, s) => sum + s.Price, 0);
      setTotalPrice(newTotalPrice);
      
      // Pass selected seats to parent component
      onSeatSelection(newSelectedSeats);
      
      return newSelectedSeats;
    });
  };

  const renderSeats = (seats, segmentIndex) => {
    return seats.map((seat, seatIndex) => (
      <React.Fragment key={seat.Code}>
        {/* 🚶‍♂️ Add space between two Aisle seats */}
        {seatIndex > 0 &&
          seats[seatIndex - 1].SeatType === 2 &&
          seat.SeatType === 2 && <Box width="20px" />}

        <Tooltip
          key={seat.Code}
          title={`Price: ₹${seat.Price || 0} | ${SEAT_DESCRIPTION[seat.Description]} | ${SEAT_TYPES[seat.SeatType]}`}
          arrow
        >
          <Box display="flex" flexDirection="column" alignItems="center" mx={1}>
            <IconButton
              onClick={() =>
                seat.AvailablityType !== 3 &&
                toggleSeatSelection(segmentIndex, seat)
              }
              disabled={
                seat.AvailablityType === 3 ||
                seat.AvailablityType === 4 ||
                seat.AvailablityType === 5
              } // ❌ Reserved seats can't be selected
              sx={{
                backgroundColor: selectedSeats[segmentIndex]?.some(
                  (s) => s.Code === seat.Code
                )
                  ? "green"
                  : seat.AvailablityType === 1
                    ? "white"
                    : "gray",
                color: selectedSeats[segmentIndex]?.some(
                  (s) => s.Code === seat.Code
                )
                  ? "white"
                  : "black",
                border: "1px solid",
                borderColor: selectedSeats[segmentIndex]?.some(
                  (s) => s.Code === seat.Code
                )
                  ? "darkgreen"
                  : "gray",
                cursor:
                  seat.AvailablityType === 3 ||
                  seat.AvailablityType === 4 ||
                  seat.AvailablityType === 5
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              <ChairIcon />
            </IconButton>
            <Typography variant="caption" fontWeight="bold">
              {seat.Code || "No Seat"}
            </Typography>
          </Box>
        </Tooltip>
      </React.Fragment>
    ));
  };

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{ zIndex: 1300 }}
    >
      <Box sx={{ width: 750, p: 3, mb: "90px" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold" fontFamily={"open sans"}>
            Select Seat
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box mt={3}>
          {seatDynamicData.length === 0 ? (
            <Typography variant="body1" color="textSecondary">
              Seat selection not available.
            </Typography>
          ) : (
            seatDynamicData.map((segment, segmentIndex) => (
              <Box key={segmentIndex} mb={4}>
                {segment.SegmentSeat.map((segmentSeat, segSeatIndex) => (
                  <Box key={segSeatIndex} mb={3}>
                    {/* 🛫 Show Origin & Destination */}
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                      {segmentSeat.RowSeats?.[0]?.Seats?.[0]?.Origin} →{" "}
                      {segmentSeat.RowSeats?.[0]?.Seats?.[0]?.Destination}
                    </Typography>

                    {/* ✈️ Show Flight Number & Aircraft Type */}
                    <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                      Flight{" "}
                      {segmentSeat.RowSeats?.[0]?.Seats?.[0]?.FlightNumber} |{" "}
                      {segmentSeat.RowSeats?.[0]?.Seats?.[0]?.CraftType}
                    </Typography>

                    {segmentSeat.RowSeats.map((row, rowIndex) => {
                      if (row.Seats?.[0]?.RowNo === "0") return null; // ❌ Skip row if RowNo = 0

                      return (
                        <Box
                          key={`${segSeatIndex}-${rowIndex}`}
                          display="flex"
                          flexWrap="wrap"
                          alignItems="center"
                          mb={2}
                        >
                          <Typography variant="subtitle2" mr={2} mt={2}>
                            Row: {row.Seats?.[0]?.RowNo}
                          </Typography>

                          <Box display="flex" gap={1.5}>
                            {row.Seats.map((seat, seatIndex) => (
                              <React.Fragment key={seat.Code}>
                                {/* 🚶‍♂️ Add space between two Aisle seats */}
                                {seatIndex > 0 &&
                                  row.Seats[seatIndex - 1].SeatType === 2 &&
                                  seat.SeatType === 2 && <Box width="40px" />}

                                {renderSeats(
                                  [seat],
                                  `${segmentIndex}-${segSeatIndex}-${rowIndex}`
                                )}
                              </React.Fragment>
                            ))}
                          </Box>
                          <Typography variant="subtitle2" mr={2} mt={2} ml={2}>
                            Row: {row.Seats?.[0]?.RowNo}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* Bottom Price Box */}
      <Box
        sx={{
          background: "#e3e3e3",
          width: "100%",
          height: "50px",
          position: "fixed",
          bottom: 0,
          padding: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Total Price: ₹{totalPrice}</Typography>
      </Box>
    </SwipeableDrawer>
  );
};

export default SeatDrawer;